import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MONTHS,
  WEEKDAY_LETTERS,
  WEEKDAY_NAMES,
  addMonths,
  buildMonthCells,
  formatHeaderDate,
  sameDay,
  startOfDay,
} from "./dateUtils";

// =============================================================================
// Painel do calendário (estilo Material 3): cabeçalho com a data escolhida, seletor de mês/ano (grade dos 12 meses, com setas/rolagem/
// deslize para trocar de ano, e grade de anos ao tocar no ano), setas de mês, dias com
// o de hoje circulado e o escolhido preenchido, e ações Limpar / Cancelar.
// Não há botão OK: clicar no dia já confirma (no `mode="range"`, o 2º clique, que
// fecha o período). Não há campo para digitar a data (o lápis foi removido).
// `mode="range"` escolhe um período (faixa entre início e fim).
//
// Só desenha e devolve valores; posicionamento fica no DatePickerPopover.
// Estilos em styles/datepicker.css (claro e escuro).
// =============================================================================

const YEAR_FROM = 1920;
const YEAR_TO = 2100;

const Chevron = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      d={dir === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Caret = ({ up }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path d={up ? "M7 14l5-5 5 5z" : "M7 10l5 5 5-5z"} fill="currentColor" />
  </svg>
);

export default function DatePickerPanel({
  mode = "single",
  value, // single: Date|null · range: { start: Date|null, end: Date|null }
  min = null,
  max = null,
  allowClear = true,
  extra = null, // conteúdo opcional entre o calendário e as ações (ex.: hora)
  title,
  onConfirm,
  onCancel,
  onClear,
}) {
  const isRange = mode === "range";
  const today = useMemo(() => startOfDay(new Date()), []);
  const initial = isRange ? value?.start || value?.end : value;

  const [selection, setSelection] = useState(() =>
    isRange
      ? { start: value?.start || null, end: value?.end || null }
      : { start: value || null, end: null },
  );
  const [cursor, setCursor] = useState(() => {
    const base = initial || today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const [view, setView] = useState("days"); // days | months | years
  const [yearCursor, setYearCursor] = useState(() => (initial || today).getFullYear());
  const [direction, setDirection] = useState(0);
  const [hover, setHover] = useState(null);
  const [focusDay, setFocusDay] = useState(null);
  const panelRef = useRef(null);
  const yearsRef = useRef(null);
  const monthsRef = useRef(null);

  const outOfRange = (date) =>
    Boolean(date) &&
    ((min && date < startOfDay(min)) || (max && date > startOfDay(max)));

  const cells = useMemo(() => buildMonthCells(cursor), [cursor]);

  // Foco no painel ao abrir (teclado e leitor de tela).
  useEffect(() => {
    const node = panelRef.current;
    if (node) node.focus({ preventScroll: true });
  }, []);

  // Grade de anos: leva o ano atual para a área visível.
  useEffect(() => {
    if (view !== "years" || !yearsRef.current) return;
    const current = yearsRef.current.querySelector('[aria-selected="true"]');
    if (current && typeof current.scrollIntoView === "function") {
      current.scrollIntoView({ block: "center" });
    }
  }, [view]);

  const goMonth = (delta) => {
    setDirection(delta);
    setCursor((c) => addMonths(c, delta));
  };

  const goYear = (delta) => {
    setDirection(delta);
    setYearCursor((y) => {
      const next = y + delta;
      return next < years[0] || next > years[years.length - 1] ? y : next;
    });
  };

  // Clique no "mês ano": dias → meses → anos → meses (o mês é o que mais se troca).
  const toggleNav = () => {
    if (view === "days") {
      setYearCursor(cursor.getFullYear());
      setDirection(0);
      setView("months");
    } else {
      setView(view === "months" ? "years" : "months");
    }
  };

  const monthOutOfRange = (year, month) =>
    Boolean(
      (min && new Date(year, month + 1, 0) < startOfDay(min)) ||
        (max && new Date(year, month, 1) > startOfDay(max)),
    );

  const pickMonth = (month) => {
    if (monthOutOfRange(yearCursor, month)) return;
    setDirection(0);
    setCursor(new Date(yearCursor, month, 1));
    setView("days");
  };

  // Clicar no dia confirma na hora (sem botão OK). No período, o 1º clique marca o
  // início e o 2º confirma início e fim. O destaque é gravado antes de confirmar
  // para o dia escolhido aparecer marcado durante a animação de fechar.
  const pickDay = (date) => {
    if (outOfRange(date)) return;
    if (!isRange) {
      setSelection({ start: date, end: null });
      onConfirm(date);
      return;
    }
    if (!selection.start || selection.end) {
      setSelection({ start: date, end: null });
      return;
    }
    const [a, b] = date < selection.start ? [date, selection.start] : [selection.start, date];
    setSelection({ start: a, end: b });
    onConfirm({ start: a, end: b });
  };

  const headerText = (() => {
    if (!isRange) {
      return selection.start ? formatHeaderDate(selection.start) : "Escolha o dia";
    }
    if (!selection.start) return "Escolha o período";
    const a = selection.start.toLocaleDateString("pt-BR", { day: "numeric", month: "short" });
    if (!selection.end) return `${a} – …`;
    const b = selection.end.toLocaleDateString("pt-BR", { day: "numeric", month: "short" });
    return `${a} – ${b}`;
  })();

  const inRangeBand = (date) => {
    if (!isRange || !date || !selection.start) return false;
    const end = selection.end || (hover && hover > selection.start ? hover : null);
    return Boolean(end) && date > selection.start && date < end;
  };

  // Teclado nos dias: setas movem, Enter/Espaço escolhe, PageUp/Down troca o mês.
  const onDaysKeyDown = (event) => {
    const base = focusDay || selection.start || today;
    const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (moves[event.key]) {
      event.preventDefault();
      const next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + moves[event.key]);
      setFocusDay(next);
      if (next.getMonth() !== cursor.getMonth() || next.getFullYear() !== cursor.getFullYear()) {
        setDirection(next > cursor ? 1 : -1);
        setCursor(new Date(next.getFullYear(), next.getMonth(), 1));
      }
    } else if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      goMonth(event.key === "PageDown" ? 1 : -1);
    } else if ((event.key === "Enter" || event.key === " ") && focusDay) {
      event.preventDefault();
      pickDay(focusDay);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onCancel?.();
    }
  };

  useEffect(() => {
    if (!focusDay || view !== "days") return;
    const node = panelRef.current?.querySelector('[data-focus="true"]');
    if (node) node.focus({ preventScroll: true });
  }, [focusDay, cursor, view]);

  const years = useMemo(() => {
    const from = min ? min.getFullYear() : YEAR_FROM;
    const to = max ? max.getFullYear() : YEAR_TO;
    return Array.from({ length: Math.max(1, to - from + 1) }, (_, i) => from + i);
  }, [min, max]);

  // Grade de meses: a roda do mouse (ou do touchpad) e o deslize horizontal trocam o ano.
  useEffect(() => {
    const node = monthsRef.current;
    if (view !== "months" || !node) return undefined;
    let lastWheel = 0;
    let touchX = null;
    const onWheel = (event) => {
      event.preventDefault();
      const now = Date.now();
      if (now - lastWheel < 220 || Math.abs(event.deltaY) + Math.abs(event.deltaX) < 4) return;
      lastWheel = now;
      goYear((Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY) > 0 ? 1 : -1);
    };
    const onTouchStart = (event) => {
      touchX = event.touches[0]?.clientX ?? null;
    };
    const onTouchEnd = (event) => {
      if (touchX == null) return;
      const dx = (event.changedTouches[0]?.clientX ?? touchX) - touchX;
      touchX = null;
      if (Math.abs(dx) > 40) goYear(dx < 0 ? 1 : -1);
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    node.addEventListener("touchstart", onTouchStart, { passive: true });
    node.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      node.removeEventListener("wheel", onWheel);
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchend", onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, years, yearCursor]); // yearCursor: a grade é recriada (key) a cada ano

  const onMonthsKeyDown = (event) => {
    if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      goYear(event.key === "PageDown" ? 1 : -1);
    }
  };

  return (
    <div
      ref={panelRef}
      className="dp-panel"
      role="dialog"
      aria-modal="true"
      aria-label={title || (isRange ? "Selecionar período" : "Selecionar data")}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <div className="dp-header">
        <div className="dp-header-title">
          {title || (isRange ? "Selecionar período" : "Selecionar data")}
        </div>
        <div className="dp-header-row">
          <div className={`dp-header-date${isRange ? " dp-header-date--range" : ""}`}>{headerText}</div>
        </div>
      </div>

      <div className="dp-nav">
        <button
          type="button"
          className="dp-month-btn"
          onClick={toggleNav}
          aria-expanded={view !== "days"}
          title={view === "days" ? "Escolher o mês" : view === "months" ? "Escolher o ano" : "Escolher o mês"}
        >
          <span>
            {view === "days" ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}` : yearCursor}
          </span>
          <Caret up={view === "years"} />
        </button>
        {view !== "years" && (
          <div className="dp-arrows">
            <button
              type="button"
              className="dp-icon-btn"
              onClick={() => (view === "months" ? goYear(-1) : goMonth(-1))}
              disabled={view === "months" && yearCursor <= years[0]}
              aria-label={view === "months" ? "Ano anterior" : "Mês anterior"}
            >
              <Chevron dir="left" />
            </button>
            <button
              type="button"
              className="dp-icon-btn"
              onClick={() => (view === "months" ? goYear(1) : goMonth(1))}
              disabled={view === "months" && yearCursor >= years[years.length - 1]}
              aria-label={view === "months" ? "Próximo ano" : "Próximo mês"}
            >
              <Chevron dir="right" />
            </button>
          </div>
        )}
      </div>

      {view === "months" ? (
        <div
          key={yearCursor}
          ref={monthsRef}
          className={`dp-months${direction > 0 ? " dp-slide-next" : direction < 0 ? " dp-slide-prev" : ""}`}
          role="listbox"
          aria-label={`Mês de ${yearCursor}`}
          onKeyDown={onMonthsKeyDown}
        >
          {MONTHS.map((name, month) => {
            const isCurrent = yearCursor === cursor.getFullYear() && month === cursor.getMonth();
            const isThisMonth = yearCursor === today.getFullYear() && month === today.getMonth();
            return (
              <button
                key={name}
                type="button"
                role="option"
                aria-selected={isCurrent}
                disabled={monthOutOfRange(yearCursor, month)}
                className={`dp-month${isCurrent ? " is-selected" : ""}${isThisMonth ? " is-today" : ""}`}
                onClick={() => pickMonth(month)}
              >
                {name}
              </button>
            );
          })}
        </div>
      ) : view === "years" ? (
        <div className="dp-years" ref={yearsRef} role="listbox" aria-label="Ano">
          {years.map((year) => (
            <button
              key={year}
              type="button"
              role="option"
              aria-selected={year === yearCursor}
              className={`dp-year${year === yearCursor ? " is-selected" : ""}${year === today.getFullYear() ? " is-today" : ""}`}
              onClick={() => {
                setYearCursor(year);
                setDirection(0);
                setView("months");
              }}
            >
              {year}
            </button>
          ))}
        </div>
      ) : (
        <div className="dp-days" onKeyDown={onDaysKeyDown}>
          <div className="dp-weekdays" aria-hidden="true">
            {WEEKDAY_LETTERS.map((l, i) => (
              <span key={i}>{l}</span>
            ))}
          </div>
          <div
            key={`${cursor.getFullYear()}-${cursor.getMonth()}`}
            className={`dp-grid${direction > 0 ? " dp-slide-next" : direction < 0 ? " dp-slide-prev" : ""}`}
            role="grid"
            onMouseLeave={() => setHover(null)}
          >
            {cells.map((date, i) => {
              if (!date) return <span key={`b${i}`} className="dp-blank" />;
              const isStart = sameDay(date, selection.start);
              const isEnd = sameDay(date, selection.end);
              const selected = isStart || isEnd;
              const disabled = outOfRange(date);
              const band = inRangeBand(date);
              const isFocus = focusDay ? sameDay(date, focusDay) : false;
              return (
                <button
                  key={date.getDate()}
                  type="button"
                  role="gridcell"
                  disabled={disabled}
                  data-focus={isFocus ? "true" : undefined}
                  tabIndex={isFocus || (!focusDay && (selected || (!selection.start && sameDay(date, today)))) ? 0 : -1}
                  aria-selected={selected}
                  aria-label={`${WEEKDAY_NAMES[date.getDay()]}, ${date.getDate()} de ${MONTHS[date.getMonth()]} de ${date.getFullYear()}`}
                  className={[
                    "dp-day",
                    selected ? "is-selected" : "",
                    sameDay(date, today) ? "is-today" : "",
                    band ? "is-band" : "",
                    isRange && isStart && selection.end ? "is-band-start" : "",
                    isRange && isEnd ? "is-band-end" : "",
                    date.getDay() === 0 || date.getDay() === 6 ? "is-weekend" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => pickDay(date)}
                  onMouseEnter={() => isRange && setHover(date)}
                >
                  <span>{date.getDate()}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {extra}

      <div className="dp-actions">
        {allowClear && (
          <button type="button" className="dp-text-btn dp-clear" onClick={onClear}>
            Limpar
          </button>
        )}
        <span className="dp-spacer" />
        <button type="button" className="dp-text-btn" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
